<script>
import { stringToColor } from '@/src/modules/global/utils/Color'

export default {
  name: 'ClientTags',
  props: {
    tags: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxTagsDisplay: {
      type: Number,
      default: null,
    },
    chipColor: {
      type: String,
      default: '#8E8E8E26',
    },
    chipTextColor: {
      type: String,
      default: '#8E8E8E',
    },
    showTextNoTag: {
      type: Boolean,
      default: false,
    },
    colorMode: {
      type: String,
      default: 'dynamic',
      validator: (value) => ['dynamic', 'fixed'].includes(value),
    },
    useSmallChips: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isAdding: false,
      newTag: '',
      dynamicDefaultBg: '#8E8E8E26',
      dynamicDefaultText: '#8E8E8E',
      fixedDefaultBg: '#e0e0e0',
      fixedDefaultText: '#4F4F4F',
    }
  },
  computed: {
    safeTags() {
      return Array.isArray(this.tags) ? this.tags : []
    },
    visibleTags() {
      if (!this.maxTagsDisplay || this.safeTags.length <= this.maxTagsDisplay) {
        return this.safeTags
      }
      return this.safeTags.slice(0, this.maxTagsDisplay)
    },
    hiddenCount() {
      return this.maxTagsDisplay && this.safeTags.length > this.maxTagsDisplay
        ? this.safeTags.length - this.maxTagsDisplay
        : 0
    },
    effectiveChipColor() {
      if (this.colorMode === 'fixed') {
        if (this.chipColor === this.dynamicDefaultBg) {
          return this.fixedDefaultBg
        }
        return this.chipColor
      }
      return this.chipColor
    },
    effectiveChipTextColor() {
      if (this.colorMode === 'fixed') {
        if (this.chipTextColor === this.dynamicDefaultText) {
          return this.fixedDefaultText
        }
        return this.chipTextColor
      }
      return this.chipTextColor
    },
    chipBgColor() {
      return (tag) => {
        if (this.isDynamicMode()) {
          return this.$setOpacity(this.getColor(tag), 0.15)
        }
        return this.effectiveChipColor
      }
    },
    chipTextColorValue() {
      return (tag) => {
        if (this.isDynamicMode()) {
          return this.getColor(tag)
        }
        return this.effectiveChipTextColor
      }
    },
    chipDeleteColor() {
      return (tag) => {
        if (this.isDynamicMode()) {
          return this.getColor(tag)
        }
        return this.effectiveChipTextColor
      }
    },
  },
  methods: {
    startAdd() {
      this.isAdding = true
      this.$nextTick(() => {
        const input = this.$el.querySelector('input')
        if (input) input.focus()
      })
    },
    confirmAdd() {
      const tag = this.newTag.trim()
      if (tag && !this.safeTags.includes(tag)) {
        this.$emit('add-tag', tag)
      }
      this.resetAdd()
    },
    cancelAdd() {
      this.resetAdd()
    },
    resetAdd() {
      this.isAdding = false
      this.newTag = ''
    },
    removeTag(tag) {
      this.$emit('remove-tag', tag)
    },
    getColor(text) {
      return stringToColor(text)
    },
    isDynamicMode() {
      return this.colorMode === 'dynamic'
    },
  },
}
</script>

<template>
  <div class="d-flex align-center flex-wrap tag-container">
    <span v-if="!visibleTags.length && showTextNoTag" class="az-text-small">
      Nenhuma tag cadastrada
    </span>

    <v-chip
      v-for="(tag, index) in visibleTags"
      v-else
      :key="index"
      class="d-flex justify-space-between ma-0 pa-0 px-3"
      style="max-height: 22px"
      :color="chipBgColor(tag)"
      :text-color="chipTextColorValue(tag)"
      :x-small="useSmallChips"
    >
      <span
        class="tag-text"
        :class="{ 'mr-4': canEdit, 'text-xxs': useSmallChips }"
      >
        {{ tag }}
      </span>
      <v-btn
        v-if="canEdit"
        icon
        x-small
        :color="chipDeleteColor(tag)"
        class="tag-delete-button ml-2"
        @click="removeTag(tag)"
      >
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </v-chip>

    <v-tooltip v-if="hiddenCount > 0" class="ma-0 pa-0" top>
      <template v-slot:activator="{ on }">
        <v-chip
          class="ma-0 pa-0 px-3"
          style="max-height: 22px"
          :color="effectiveChipColor"
          :text-color="effectiveChipTextColor"
          pill
          :x-small="useSmallChips"
          v-on="on"
        >
          +{{ hiddenCount }}
        </v-chip>
      </template>

      <div v-for="(tag, i) in safeTags.slice(maxTagsDisplay)" :key="i">
        <span>{{ tag }}</span
        ><br />
      </div>
    </v-tooltip>

    <template v-if="canEdit">
      <div v-if="isAdding" class="ml-1">
        <v-text-field
          v-model="newTag"
          hide-details
          type="text"
          placeholder="Nova tag"
          dense
          class="tag-input"
          outlined
          @keyup.enter="confirmAdd"
          @keyup.esc="cancelAdd"
          @blur="cancelAdd"
        >
          <template v-slot:append>
            <v-btn icon x-small color="primary" @click="confirmAdd">
              <v-icon small>mdi-send</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>

      <div v-else class="ml-1">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn :loading="loading" icon small v-on="on" @click="startAdd">
              <icon-svg name="add-circle" size="24" color="var(--muted)" />
            </v-btn>
          </template>
          <span>Adicionar tag</span>
        </v-tooltip>
      </div>
    </template>
  </div>
</template>

<style lang="sass">
.tag-container
  gap: 3px

  // Remove margens nativas do v-chip para que o gap do container controle o
  // espaçamento uniformemente (evita espaço extra antes do chip "+N").
  .v-chip
    margin: 0 !important

  // O chip "+N" é renderizado dentro de um wrapper <span class="v-tooltip">.
  // Sem normalizar, esse span entra como flex-item "caixa" e desalinha/abre um
  // gap extra. Faz o wrapper se comportar como os demais chips.
  .v-tooltip
    display: inline-flex
    align-items: center
    margin: 0 !important

  .tag-text
    font-size: 12px
    font-weight: 600
    font-family: 'Geist', sans-serif

  .v-chip
    position: relative

    .tag-delete-button
      position: absolute
      right: 4px
      top: 2px

  .tag-input
    display: flex
    align-items: center
    width: 120px !important
    min-height: 28px !important
    max-height: 28px !important
    border-spacing: 0px !important

    .v-input__control
      .v-input__slot
        min-height: 28px !important
        max-height: 28px !important
        height: 28px !important
        padding: 0 8px !important

    input
      min-height: 28px
      line-height: 28px
      padding: 0 !important

    .v-input__append-inner
      display: flex
      align-items: center
      margin: 0 !important
      margin-block: auto !important
</style>
